/**
 * API client with Clerk authentication interceptors
 */
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.codicecivile.ai/api/v1';

// Token getter function that will be set by the app
let tokenGetter: (() => Promise<string | null>) | null = null;

/**
 * Set the token getter function (should be called from Clerk's useAuth)
 */
export function setTokenGetter(getter: () => Promise<string | null>) {
  tokenGetter = getter;
}

/**
 * Get the token getter function (for streaming and other direct fetch calls)
 */
export function getTokenGetter(): (() => Promise<string | null>) | null {
  return tokenGetter;
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Include cookies for Clerk session
    });

    // Request interceptor to add Clerk auth token
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        try {
          // Get token from Clerk
          if (tokenGetter) {
            const token = await tokenGetter();
            if (token && config.headers) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          }
        } catch (error) {
          console.error('Failed to get Clerk token:', error);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        // If 401, let Clerk handle the redirect to login
        if (error.response?.status === 401) {
          console.error('Authentication failed - user may need to sign in again');
        }
        return Promise.reject(error);
      }
    );
  }

  get instance() {
    return this.client;
  }
}

export const apiClient = new ApiClient().instance;
